<template>
  <div class="transfer-chat__content" v-if="!transferringChat">
    <header>
      <div>
        <h3>{{ $t('agent-console.transfer-chat.title')}}</h3>
        <el-button type="text" @click="handleGoBack">
          <i class="fas fa-times"></i>
        </el-button >
      </div>
      <p v-if="!isWidget">{{ $t('agent-console.transfer-chat.select-where-you-want-to-transfer-the-chat-to')}}</p>
    </header>
    <el-radio-group v-model="filter" :size="isWidget ? 'small' : 'medium '">
      <el-radio-button v-for="type in ['agent', 'department', 'bot']" :label="type" :key="type">
        {{ $t(`agent-console.transfer-chat.${type}`) }}
      </el-radio-button>
    </el-radio-group>
    <the-search-input v-model="search" />
    <p v-if="search && list.length === 0">
      {{ $t('agent-console.transfer-chat.no-search-results', { search }) }} 
    </p>
    <div class="list" v-loading="showLoading" :element-loading-text="loadingText">
      <label type="radio" v-for="item in list" :label="item.id" :key="item.id">
        <input type="radio" :value="item.id" v-model="value" :key="item.id" />
        <i class="fas" :class="{
          'fa-headset': filter === 'agent',
          'fa-robot': filter === 'bot',
          'fa-building': filter === 'department'
        }"></i>
        <div>
          <div>{{ item.name }}</div>
          <div v-if="filter === 'agent'">
            <p class="agent-status" :class="{ 'available' : item.extra.available, 'unavailable' : !item.extra.available}">
              {{ item.extra.available ? $t('agent-console.transfer-chat.available') : $t('agent-console.transfer-chat.unavailable') }}
            </p>
          </div>
        </div>
      </label>
    </div>
    <el-button type="primary" :disabled="!value" @click="handleTransferChat">{{ $t('agent-console.transfer-chat.transfer-chat') }}</el-button>
  </div>
  <div v-else class="transfer-chat__transferring">
    <p v-if="!errorTransfer && !successTransfer">
      <i class="fas fa-circle-notch fa-spin"></i>
      {{ $t('agent-console.transfer-chat.transferring-chat-to', { name : selectedValue.name }) }}
    </p>
    <div v-if="successTransfer" class="transfer-chat__success-message-container transfer-chat__message-container">
      <img src="@/img/success.svg" class="img-message mb-5" />
      <p class="success-message">{{ $t('agent-console.transfer-chat.chat-transferred-successfully', { name: selectedValue.name }) }} </p>
    </div>
    <div v-if="errorTransfer" class="transfer-chat__error-message-container transfer-chat__message-container">
      <img src="@/img/error.svg" class="img-message mb-5" />
      <p class="error-message">{{ $t('agent-console.transfer-chat.the-chat-could-not-be-transferred') }} </p>
    </div>
    <el-button v-if="errorTransfer || successTransfer" @click="handleGoBack">{{ $t('common.go-back') }}</el-button>
  </div>
</template>

<script>
import http from '@/api/http'
import TheSearchInput from '@/components/html/TheSearchInput.vue'
export default {
  props: {
    isWidget: {
      type: Boolean,
      default: false,
      required: false
    }
  },
  data () {
    return {
      search: '',
      value: 0,
      filter: 'agent',
      fetchingBots: false,
      fetchingDepartments: false,
      bots: [],
      departments: [],
      transferringChat: false,
      errorTransfer: null,
      successTransfer: null,
      lastFetchedBots: null,
      lastFetchedDepartments: null
    }
  },
  computed: {
    agent () {
      return this.$store.getters['getAgent']
    },
    agents () {
      return this.$store.getters['getAgentsDashboard']
        .filter(agent => agent.chat_logged && agent.have_chat_center && agent.chatDepartments.includes(parseInt(this.chat.data.dept_id)))
        .filter((agent) => {

          if (!this.search) {
            return agent
          }

          return agent.name.toLowerCase().includes(this.search.toLowerCase()) || agent.number.toString().includes(this.search.toLowerCase())
        }).map((agent) => ({
          id: agent.chat_center_id, 
          name: agent.name, 
          extra: {
            available: agent.available_for_chats ?? true
          }
        }))
    },
    botsList () {
      return this.bots.filter(bot => {
        if (!this.search) {
          return bot
        }

        return bot.name.toLowerCase().includes(this.search.toLowerCase())
      }).map((bot) => ({ id: bot.id, name: bot.name }))
    },
    departmentsList () {
      return this.departments.filter(department => {
        if (!this.search) {
          return department
        }

        return department.name.toLowerCase().includes(this.search.toLowerCase())
      }).map((department) => ({ id: department.id, name: department.name }))
    },
    list () {
      if (this.filter === 'bot') {
        return this.botsList
      }

      if (this.filter === 'department') {
        return this.departmentsList
      }
      return this.agents
    },
    queryParams () {
      return { ["organization.id"]: this.agent.organization_id, pagination: false }
    },
    showLoading () {
      return (this.fetchingBots && this.filter === 'bot') || (this.fetchingDepartments && this.filter === 'department')
    },
    selectedValue () {
      return this.list.find(item => item.id === this.value)
    },
    chat() {
      return this.$store.getters['getActiveItem']
    },
    loadingText() {
      if (this.filter === 'bot') {
        return this.$t('agent-console.transfer-chat.loading-bots')
      }

      if (this.filter === 'department') {
        return this.$t('agent-console.transfer-chat.loading-departments')
      }

      return this.$t('agent-console.transfer-chat.loading-agents')
    }
  },
  methods: {
    async fetchBots () {
      if (this.fetchingBots || this.lastFetchedBots && this.lastFetchedBots > new Date(Date.now() - 1000 * 60 * 5)) {
        return
      }

      this.lastFetchedBots = new Date()
      this.fetchingBots = true
      const { data } = await http.get('bots/basic', { params: this.queryParams })
      this.bots = data['hydra:member']
      this.fetchingBots = false
    },
    async fetchDepartments () {
      if (this.fetchingDepartments || this.lastFetchedDepartments && this.lastFetchedDepartments > new Date(Date.now() - 1000 * 60 * 5)) {
        return
      }

      this.lastFetchedDepartments = new Date()
      this.fetchingDepartments = true
      const { data } = await http.get('departments/basic', { params: this.queryParams })
      this.departments = data['hydra:member']
      this.fetchingDepartments = false
    },
    async handleTransferChat () {
      this.transferringChat = true

      const snapshot = await this.$store.dispatch('doChatTransfer', {
        destination: this.selectedValue.id, 
        roomId: this.chat.key, 
        transferType: this.filter
      })

      this.$store.dispatch("agentConsoleChat/listenToChatEngineAction", {
        key: snapshot.key,
        callback: (data) => {
          if (!data.status) {
            return
          }

          this.successTransfer = data.status === 'success'
          this.errorTransfer = !this.successTransfer
        }
      });
    },
    handleGoBack() {
      this.$emit('chatTransferred')
      this.resetComponent()
    },
    resetComponent() {
      this.search =  '',
      this.value =  0,
      this.filter =  'agent',
      this.fetchingBots =  false,
      this.fetchingDepartments =  false,
      this.transferringChat =  false,
      this.errorTransfer =  null,
      this.successTransfer =  null
    }

  },
  components: {
    TheSearchInput
  },
  watch: {
    filter (value) {
      if (value === 'bot') {
        this.fetchBots()
      }

      if (value === 'department') {
        this.fetchDepartments()
      }

      this.value = 0
    },
    transferringChat(value) {
      if (value) {
        this.$emit('denyClose')
      } else {
        this.$emit('allowClose')
      }
    }
  }
}
</script>
<style>

@media screen and (max-width: 400px) {
  .transfer-chat__content .el-radio-group{
    display: flex;
  }
  .transfer-chat__content .list label{
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 10px;
      padding: 5px;
      border: 1px solid #ccc;
  }
}
</style>
<style scoped>
header > div {
  display: flex;
  flex-direction: row;
  justify-content:space-between;
  align-items:center;
}

.transfer-chat__content{
  display: flex;
  flex-direction: column;
  gap: var(--column);
  height: 100vh;
  padding: var(--column);
}

.transfer-chat__transferring {
  display: flex;
  flex-direction: column;
  gap: var(--column);
  justify-content: center;
  align-items: center;
  height: 100vh;
}

.list {
  display: flex;
  flex-direction: column;
  gap: calc(var(--column) / 2);
  overflow-y: auto;
  flex: 1
}

.list label {
  display: flex;
  align-items: center;
  gap: calc(var(--column) / 2);
  border-radius: var(--border-radius);
  padding: var(--column);
  border: 1px solid #DCDFE6;
  border-radius: 4px;
}

label .agent-status {
  font-size: 12px
}

label .agent-status.available {
  color: var(--green)
}

label .agent-status.unavailable {
  color: var(--red)
}

img {
  width: 5em;
  margin: var(--column) auto;
  display: block;
}

@media screen and (max-width: 400px) {
  .transfer-chat__content .list label{
      display: flex;
      align-items: center;
      padding: 10px;
      border: 1px solid #ccc;
      font-size: 14px;
  }
  .transfer-chat__content .list{
    gap: 5px;
    max-height: 200px;
  }
  .transfer-chat__content {
    height: 100%;
  }
  .transfer-chat__transferring .transfer-chat__message-container img{
    width: calc(100vw - 100px);
  }
  .transfer-chat__transferring .transfer-chat__message-container p{
    text-align: center;
  }
  .transfer-chat__transferring{
    height: 100%;
  }
}

</style>